import { forwardRef, type ButtonHTMLAttributes } from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { LucideIcon } from "lucide-react";
import { pressHandlers } from "./press";
import pressable from "./pressable.module.css";
import styles from "./IconButton.module.css";

function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * IconButton: a square, icon-only control.
 *
 * Variants:
 *  - `ghost`   transparent until hover. Default for toolbars.
 *  - `surface` tonal fill with a hairline border.
 *  - `primary` flat accent fill.
 *
 * `aria-label` is required. `tooltip` sets the native `title`. The hit area
 * is always extended to 40px. State styling is CSS-driven; see `Button`.
 */

export type IconButtonVariant = "primary" | "ghost" | "surface";
export type IconButtonSize = "sm" | "md" | "lg";

export interface IconButtonProps
    extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "aria-label"> {
    icon: LucideIcon;
    variant?: IconButtonVariant;
    size?: IconButtonSize;
    disabled?: boolean;
    /** Native tooltip text. Defaults to no tooltip. */
    tooltip?: string;
    "aria-label": string;
}

const iconSizes: Record<IconButtonSize, number> = {
    sm: 16,
    md: 20,
    lg: 24,
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    function IconButton(props, ref) {
        const {
            icon: Icon,
            variant = "ghost",
            size = "md",
            disabled = false,
            tooltip,
            className,
            "aria-label": ariaLabel,
            onPointerDown,
            onPointerUp,
            onPointerCancel,
            onPointerLeave,
            onKeyDown,
            onKeyUp,
            onBlur,
            ...rest
        } = props;

        const press = pressHandlers<HTMLButtonElement>(
            { onPointerDown, onPointerUp, onPointerCancel, onPointerLeave, onKeyDown, onKeyUp, onBlur },
            disabled,
        );

        return (
            <button
                ref={ref}
                disabled={disabled}
                data-variant={variant}
                data-size={size}
                data-hit-area=""
                aria-label={ariaLabel}
                title={tooltip}
                className={cn(pressable.pressable, styles.root, className)}
                {...press}
                {...rest}
            >
                <Icon size={iconSizes[size]} aria-hidden="true" />
            </button>
        );
    },
);
